/** 웹 화면이 읽는 판매처 규격 값.
 *
 * ⚠️ **판매처 고유값을 이 파일에 박지 않는다.** `860` · `1000×1000` · 블록 목록 · 파일
 * 이름 규칙 · 내보내기 형식은 전부 네이버가 정한 값이고 정본은 `docs/TEMPLATE.md` 다.
 * 코드가 그 값을 읽는 자리는 `packages/shared` 의 `channel.ts`(`SMARTSTORE_SPEC` ·
 * `CHANNEL_SPECS`) 하나다. 출시 뒤 판매처가 늘면 여기가 아니라 그 레코드가 늘어난다
 * (PRD 7.7.9). 이 파일은 그 레코드를 화면이 쓰는 모양으로 읽어 줄 뿐이다.
 *
 * ⛔ **예시 데이터를 되살리지 마라** (2026-10-04 · 10-05 사용자 지시). 이 파일은 예전에
 * `lib/sample-data.ts` 였고, 백엔드가 없던 화면을 세우려고 목업의 값(남의 상품, 가짜 작업 ·
 * 알림 · 이력 · 동의 기록)을 옮겨 두던 곳이었다. 그 값을 전부 걷은 뒤 판매처 규격만 남아
 * 이름도 그것에 맞췄다(WEB-SAMPLE-DATA-REMNANT). **실제 값이 없는 자리는 그리지 않는다.**
 * 화면을 세우려고 값이 필요하면 서버 응답을 받거나, 그 자리를 비운다. 목업의 값은 목업에 있다.
 * 모든 작업 id 는 서버로 간다. 예시 id 를 가로채는 갈래를 되살리지 않는다.
 */

import { DEFAULT_SALES_CHANNEL, channelBlockSpec, channelSpec, type BlockType } from "@makit/shared";

/** 지금 화면이 그리는 판매처. 고르기는 아직 없다(PRD 7.13 D-36). 기본값 하나를 읽는다. */
export const CHANNEL = DEFAULT_SALES_CHANNEL;
export const SPEC = channelSpec(CHANNEL);

/* ── W-30 검토 · 고치기 ──────────────────────────────────── */

/** 미리보기 페이지 폭(`.page`). 축소율은 이 값과 판매처 캔버스 폭에서 나온다.
 *  `47%` 를 문구에 박으면 캔버스 폭이 바뀔 때 조용히 거짓말이 된다. */
const PREVIEW_PAGE_WIDTH = 400;

/** 미리보기 머리의 캔버스 폭과 축소율 표기. */
export const CANVAS_SCALE_LABEL = `가로 ${SPEC.canvasWidth}px · ${Math.round(
  (PREVIEW_PAGE_WIDTH / SPEC.canvasWidth) * 100,
)}% 축소`;

/** 이미지 한 장의 세로 상한. **판매처가 정한 값이다.** */
export const MAX_IMAGE_HEIGHT_PX = SPEC.maxImageHeight;

export const LOCKED_ITEMS: Array<[string, string]> = [
  ["여백과 정렬", "판매처 규격에 맞춘 고정값입니다"],
  ["글자 크기·위치", "상품마다 달라지면 매대에서 흐트러집니다"],
  ["피사체 크기", "캔버스의 78%로 고정입니다"],
  ["사진 자르기·회전·보정", "다시 찍는 편이 빠릅니다"],
  ["블록 순서와 종류", "순서가 곧 전환율이라 틀이 정합니다"],
  ["폰트·레이어·자유 배치", "열면 포토샵의 열등한 복제품이 됩니다"],
];

/* ── W-40 내보내기 ───────────────────────────────────────── */

/** 미리보기 블록 배지의 크기 표기. 규격에서 읽는다. */
export function blockDimLabel(block: BlockType, grownHeight?: number): string {
  const blockSpec = channelBlockSpec(CHANNEL, block);
  const width = blockSpec?.width ?? SPEC.canvasWidth;
  const height = blockSpec?.height ?? grownHeight ?? 0;
  return `${width}×${height}`;
}
